import { activeWindowForHost } from '../hostApplications';
import {
  SandboxHost,
  SandboxHostGroup,
  SandboxSession,
  SandboxTariff,
} from '../types';

const colors = [
  '#5762D5',
  '#A855F7',
  '#F97316',
  '#0EA5E9',
  '#F59E0B',
  '#EF4444',
];
const titles = [
  'Общий зал',
  'VIP',
  'TRIO',
  'BOOTCAMP',
  'Консоли',
  'SOLO LEVELING',
];

type HostSpec = {
  groupId: number;
  position: number;
  type: 'PC' | 'CONSOLE';
  x: number;
  y: number;
};

const hostSpecs: HostSpec[] = [
  ...[
    [1, 5, 5],
    [2, 6, 5],
    [8, 7, 5],
    [10, 8, 5],
    [11, 9, 5],
    [7, 5, 7],
    [5, 6, 7],
    [13, 7, 7],
    [14, 8, 7],
    [3, 9, 7],
    [4, 5, 9],
    [16, 6, 9],
    [9, 7, 9],
    [6, 8, 9],
    [15, 9, 9],
  ].map(([position, x, y]) => ({
    groupId: 1,
    position,
    type: 'PC' as const,
    x,
    y,
  })),
  ...[
    [2, 2, 2],
    [3, 2, 3],
    [4, 2, 4],
    [8, 2, 5],
    [6, 2, 7],
    [7, 2, 8],
  ].map(([position, x, y]) => ({
    groupId: 2,
    position,
    type: 'PC' as const,
    x,
    y,
  })),
  ...[
    [4, 6, 2],
    [12, 7, 2],
    [5, 8, 2],
  ].map(([position, x, y]) => ({
    groupId: 3,
    position,
    type: 'PC' as const,
    x,
    y,
  })),
  ...[
    [6, 2, 10],
    [7, 2, 11],
    [8, 6, 15],
    [5, 7, 15],
    [2, 8, 15],
    [3, 9, 15],
    [1, 10, 15],
  ].map(([position, x, y]) => ({
    groupId: 4,
    position,
    type: 'PC' as const,
    x,
    y,
  })),
  ...[
    [4, 13, 2],
    [3, 13, 3],
    [2, 13, 5],
    [1, 13, 6],
  ].map(([position, x, y]) => ({
    groupId: 5,
    position,
    type: 'CONSOLE' as const,
    x,
    y,
  })),
  ...[{ groupId: 6, position: 1, type: 'PC' as const, x: 13, y: 15 }],
];

const hostGroupById = new Map(
  hostSpecs.map((spec, index) => [index + 1, spec.groupId])
);
const megabytesPerGigabyte = 1024;

const createDiskStatus = (
  hostId: number
): SandboxHost['counters']['disk_status'] => {
  const totalGigabytes = [2048, 2560, 3072, 3584, 4096][(hostId - 1) % 5];
  const systemUsedGigabytes = 190 + ((hostId * 7) % 31);
  const serviceUsedGigabytes = 8 + ((hostId * 3) % 7);
  const gamesTotalGigabytes = totalGigabytes - 350;
  const gamesUsedGigabytes = Math.floor(
    gamesTotalGigabytes * (0.7 + (hostId % 11) / 100)
  );

  return [
    {
      letter: 'C',
      total: 300 * megabytesPerGigabyte,
      used: systemUsedGigabytes * megabytesPerGigabyte,
    },
    {
      letter: 'D',
      total: 50 * megabytesPerGigabyte,
      used: serviceUsedGigabytes * megabytesPerGigabyte,
    },
    {
      letter: 'E',
      total: gamesTotalGigabytes * megabytesPerGigabyte,
      used: gamesUsedGigabytes * megabytesPerGigabyte,
    },
  ];
};

export const createHostGroups = (): SandboxHostGroup[] =>
  titles.map((title, index) => ({
    __typename: 'HostGroupOverview',
    origin: 'preset',
    id: index + 1,
    title,
    priority: index + 1,
    customization: { color: colors[index] },
  }));

export const createHosts = (): SandboxHost[] =>
  hostSpecs.map((spec, index) => {
    const id = index + 1;
    const { groupId, position, type, x, y } = spec;
    const isConsole = type === 'CONSOLE';
    return {
      __typename: 'HostOverview' as const,
      origin: 'preset' as const,
      id,
      group_id: groupId,
      type,
      position,
      alias: isConsole
        ? `PS5-${String(position).padStart(2, '0')}`
        : `PC-${String(id).padStart(2, '0')}`,
      coord_x: x,
      coord_y: y,
      in_service: id === 12 || id === 28,
      online: ![9, 17, 33].includes(id),
      online_booking_enabled: true,
      shell_mode: id === 17 ? 'DISABLED' : 'NORMAL',
      device_has_changed: id === 21,
      locked: id === 31,
      shell_client_uuid: null,
      mac_addr: `02:00:00:00:${String(groupId).padStart(2, '0')}:${String(
        id
      ).padStart(2, '0')}`,
      ip_addr: `192.168.${groupId}.${100 + position}`,
      qr: `sandbox-host-${id}`,
      comment: id === 12 ? 'Плановая профилактика' : '',
      info: {
        shell_version: isConsole ? null : '1.3.5.0',
        processor: isConsole ? 'AMD Zen 2' : 'Intel Core i5-14600KF',
        ram: isConsole ? '16 GB' : '32 GB DDR5',
        video: isConsole ? 'AMD RDNA 2' : 'NVIDIA GeForce RTX 4070',
        disc: '1 TB NVMe',
      },
      counters: {
        active_window: 'SmartShell',
        disk_status: createDiskStatus(id),
      },
    };
  });

export const createSessions = (
  anchorMs: number,
  tariffs: SandboxTariff[]
): SandboxSession[] => {
  const hostIds = [
    1, 2, 3, 4, 5, 6, 7, 8, 14, 15, 16, 18, 20, 21, 22, 24, 26, 27, 29, 30, 35,
  ];
  const elapsedValues = hostIds.map((_, index) =>
    index === hostIds.length - 1 ? 18000 : 900 + index * 810
  );
  const perMinuteTariff = tariffs.find((tariff) => tariff.per_minute);
  if (!perMinuteTariff) throw new Error('Поминутный seed-тариф не найден');

  return hostIds.map((hostId, index) => {
    const hostGroupId = hostGroupById.get(hostId);
    if (!hostGroupId) throw new Error(`Неизвестная группа хоста ${hostId}`);
    const elapsed = elapsedValues[index];
    const packageTariff = tariffs
      .filter(
        (tariff) =>
          !tariff.per_minute &&
          tariff.duration > elapsed &&
          tariff.host_group_ids.includes(hostGroupId)
      )
      .sort((left, right) => left.duration - right.duration)[0];
    if (!packageTariff) throw new Error('Пакетный seed-тариф не найден');
    const postpaid = index === 17 || index === 21;
    const tariff = postpaid ? perMinuteTariff : packageTariff;
    const startedAt = new Date(anchorMs - elapsed * 1000).toISOString();
    const duration = postpaid ? 0 : tariff.duration;

    return {
      __typename: 'ClientSessionOverview',
      origin: 'preset',
      id: 400 + index,
      client_uuid: index < 8 ? `preset:client:${index + 1}` : null,
      host_id: hostId,
      host_ids: [hostId],
      postpaid,
      status: 'ACTIVE',
      started_at: startedAt,
      finished_at: postpaid
        ? null
        : new Date(Date.parse(startedAt) + duration * 1000).toISOString(),
      duration,
      elapsed,
      time_left: postpaid ? 0 : Math.max(0, duration - elapsed),
      total_cost: postpaid ? (index + 1) * 75 : tariff.cost,
      seances: [
        {
          id: 800 + index,
          status: 'ACTIVE',
          tariff: {
            id: tariff.id,
            title: tariff.title,
            kind: tariff.kind,
          },
        },
      ],
    };
  });
};

export const applySeedHostApplications = (
  hosts: SandboxHost[],
  sessions: SandboxSession[]
): SandboxHost[] =>
  hosts.map((host) => ({
    ...host,
    counters: {
      ...host.counters,
      active_window: activeWindowForHost(host.id, sessions),
    },
  }));
